import { env } from '../config/environment';
import { authorizedRequest } from './authorizedFetch';

/**
 * Para on the client file (docs/plans/PARA_AGENT_PLAN.md Stage 4; server: server/lib/guidedAdvice/routes.cjs).
 * Shapes are checked: Firebase Hosting answers an unknown path with index.html and a 200.
 */

/** Whether this person sees the floating Para button (Para access, and the firm's switch on). */
export async function fetchParaClientFileEnabled({ signal } = {}) {
  const data = await authorizedRequest(env.apiEndpoints.paraClientFile, { signal }, 'Para could not be checked.');
  return Boolean(data && data.enabled === true);
}

/** Whether Para opens on this client: in a firm running the Para trial, only on trial clients. */
export async function fetchParaOpens(clientId, { signal } = {}) {
  const data = await authorizedRequest(env.apiEndpoints.paraOpens(clientId), { signal }, 'Para could not be checked.');
  return Boolean(data && data.opens === true);
}

/**
 * One question about the client's file.
 * @param {string} clientId
 * @param {{ text: string, history?: { role: 'adviser'|'assistant', text: string }[], signal?: AbortSignal }} args
 * @returns {Promise<{ reply: string, openIn: ''|'review', note: { title, category, body }|null }>}
 */
export async function sendParaClientTurn(clientId, { text, history = [], signal }) {
  const data = await authorizedRequest(
    env.apiEndpoints.clientParaTurn(clientId),
    { method: 'POST', body: { text, history }, signal },
    'Para could not answer.',
  );
  if (!data || typeof data.reply !== 'string') {
    const error = new Error('Para’s answer came back in an unexpected shape — is the backend up to date?');
    error.code = 'bad-shape';
    throw error;
  }
  const note = data.note && typeof data.note.title === 'string' && typeof data.note.body === 'string' ? data.note : null;
  // A drafted email or SMS (Stage 8): opened for the adviser to send, never sent from here.
  const m = data.message;
  const message = m && (m.channel === 'email' || m.channel === 'sms') && typeof m.body === 'string' && m.body.trim()
    ? { channel: m.channel, to: m.to || 'client', subject: typeof m.subject === 'string' ? m.subject : '', body: m.body }
    : null;
  // Documents and envelopes to run straight away (Stage 7); each is checked again when it runs.
  const actions = Array.isArray(data.actions)
    ? data.actions.filter((a) => a && typeof a.kind === 'string' && a.params && typeof a.params === 'object')
    : [];
  return { reply: data.reply, openIn: data.openIn === 'review' ? 'review' : '', note, message, actions };
}

/**
 * Run one client-file action (src/utils/paraFileActions.js): a file note the adviser posts, or a document
 * or envelope Para's reply asked for. A document refused for missing facts throws them as `error.missing`.
 * @returns {Promise<{ kind: string, id: string, name?: string }>} what the card shows and Undo needs
 */
export async function runParaFileAction(clientId, { kind, params }) {
  const data = await authorizedRequest(
    env.apiEndpoints.clientParaFileActions(clientId),
    { method: 'POST', body: { kind, params } },
    'Para couldn’t do that.',
  );
  // A task that completes in its own window comes back with no id: nothing was done, so nothing to undo.
  if (!data || (typeof data.id !== 'string' && data.openTask !== true)) throw new Error('Para’s answer came back in an unexpected shape — is the backend up to date?');
  return data;
}

/** Undo a client-file action (a note is removed only while it is untouched). */
export async function undoParaFileAction(clientId, { kind, id }) {
  const data = await authorizedRequest(
    `${env.apiEndpoints.clientParaFileActions(clientId)}/undo`,
    { method: 'POST', body: { kind, id } },
    'That couldn’t be undone.',
  );
  if (!data || data.undone !== true) throw new Error('Para’s answer came back in an unexpected shape — is the backend up to date?');
  return true;
}
